import React from 'react';
import { Box, Container, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Hero from '../components/Hero';
import Services from '../components/Services';
import SectionHeading from '../components/SectionHeading';
import RequestQuoteForm from '../components/RequestQuoteForm';
import BeforeAfter from '../components/BeforeAfter';
import Gallery from '../components/Gallery';
import ImagesProvider from '../contexts/ImagesProvider';
import FeedbacksList from '../components/FeedbacksList';
import FeedbackForm from '../components/FeedbackForm';
import FeedbacksProvider from '../contexts/FeedbacksProvider';
import Footer from '../components/Footer';
import { company, serviceNames } from '../data/company';
import { brand, serif } from '../theme';

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(12, 0),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(8, 0),
    },
  },
  dark: {
    backgroundColor: brand.ink,
    color: '#FFFFFF',
  },
  white: {
    backgroundColor: '#FFFFFF',
    borderTop: `1px solid ${brand.line}`,
  },
  approachHeading: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  statement: {
    fontFamily: serif,
    fontWeight: 400,
    fontSize: 'clamp(1.75rem, 4vw, 3.25rem)',
    lineHeight: 1.14,
    letterSpacing: '-0.015em',
    maxWidth: 980,
    margin: theme.spacing(0, 0, 8),
    '& em': {
      fontStyle: 'italic',
      color: '#E2A58F',
    },
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(5),
    },
  },
  principles: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    borderTop: '1px solid rgba(255,255,255,0.18)',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
  },
  principle: {
    padding: theme.spacing(4, 0),
    borderBottom: '1px solid rgba(255,255,255,0.18)',
    '&:last-child': {
      borderBottom: 0,
      paddingBottom: 0,
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4, 4, 0),
      borderBottom: 0,
      borderLeft: '1px solid rgba(255,255,255,0.18)',
      '&:first-child': {
        paddingLeft: 0,
        borderLeft: 0,
      },
    },
  },
  principleNumber: {
    color: '#E2A58F',
    display: 'block',
    marginBottom: theme.spacing(2),
  },
  principleTitle: {
    marginBottom: theme.spacing(1),
  },
  principleText: {
    color: 'rgba(255,255,255,0.7)',
  },
  projectsHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    alignItems: 'end',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr',
      gap: theme.spacing(8),
    },
  },
  projectsNote: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(5),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(7),
    },
  },
  galleryWrap: {
    marginTop: theme.spacing(3),
  },
  splitGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '5fr 7fr',
      gap: theme.spacing(10),
    },
  },
  details: {
    margin: 0,
    borderTop: `1px solid ${brand.ink}`,
  },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${brand.line}`,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gap: 2,
    },
  },
  detailLabel: {
    color: theme.palette.text.secondary,
    margin: 0,
  },
  detailValue: {
    margin: 0,
    fontWeight: 500,
    '& a': {
      color: 'inherit',
      textDecorationColor: brand.accent,
      textUnderlineOffset: '4px',
    },
  },
}));

const principles = [
  { title: 'One point of contact', text: 'We coordinate every trade on your project, so a single team is accountable for the result.' },
  { title: 'Free, honest estimates', text: 'Tell us what you have in mind and we will put together a clear quote at no cost to you.' },
  { title: 'Local to the area', text: 'We know the homes, the weather and the expectations of clients across Seattle and nearby cities.' },
];

const contactDetails = [
  { label: 'Phone', value: <a href={company.phoneHref}>{company.phoneDisplay}</a> },
  { label: 'Service area', value: company.area },
  { label: 'Estimates', value: 'Free, no obligation' },
];

export default function HomePage() {
  const classes = useStyles();

  return (
    <ImagesProvider>
      <main>
        <Hero />

        <Services />

        <Box component="section" id="about" className={`${classes.section} ${classes.dark}`}>
          <Container maxWidth="lg">
            <SectionHeading light index="02" eyebrow="Our approach" className={classes.approachHeading} />
            <h2 className={classes.statement}>
              {company.name} is a general contractor for construction projects big and small,
              with the resources to take on <em>whatever your project needs.</em>
            </h2>
            <div className={classes.principles}>
              {principles.map((principle, index) => (
                <div key={principle.title} className={classes.principle}>
                  <Typography variant="overline" component="span" className={classes.principleNumber}>
                    {String(index + 1).padStart(2, '0')}
                  </Typography>
                  <Typography variant="h5" component="h3" className={classes.principleTitle}>
                    {principle.title}
                  </Typography>
                  <Typography variant="body2" className={classes.principleText}>
                    {principle.text}
                  </Typography>
                </div>
              ))}
            </div>
          </Container>
        </Box>

        <Box component="section" id="projects" className={classes.section}>
          <Container maxWidth="lg">
            <div className={classes.projectsHeader}>
              <SectionHeading index="03" eyebrow="Selected work" title="Recent projects" />
              <Typography className={classes.projectsNote}>
                Drag the slider to see a project before and after, or tap any photo
                below to view it full size.
              </Typography>
            </div>
            <BeforeAfter />
            <div className={classes.galleryWrap}>
              <Gallery />
            </div>
          </Container>
        </Box>

        <Box component="section" id="reviews" className={`${classes.section} ${classes.white}`}>
          <Container maxWidth="lg">
            <FeedbacksProvider>
              <div className={classes.splitGrid}>
                <div>
                  <SectionHeading index="04" eyebrow="Reviews" title="In our customers' words." />
                  <FeedbackForm />
                </div>
                <FeedbacksList />
              </div>
            </FeedbacksProvider>
          </Container>
        </Box>

        <Box component="section" id="quote" className={classes.section}>
          <Container maxWidth="lg">
            <div className={classes.splitGrid}>
              <div>
                <SectionHeading
                  index="05"
                  eyebrow="Contact"
                  title="Start your project."
                  subtitle="Tell us what you have in mind. We'll follow up to talk through the details and set up a free estimate."
                />
                <dl className={classes.details}>
                  {contactDetails.map((detail) => (
                    <div key={detail.label} className={classes.detailRow}>
                      <dt className={classes.detailLabel}>{detail.label}</dt>
                      <dd className={classes.detailValue}>{detail.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div id="quote-form">
                <RequestQuoteForm services={serviceNames} />
              </div>
            </div>
          </Container>
        </Box>

        <Footer />
      </main>
    </ImagesProvider>
  );
}
